import { useEffect, useRef, useState } from 'react'

import { Icon } from './Icon'

/**
 * Слот для фотографии приёмки.
 *
 * Компонент не хранит File и не строит превью сам: и то и другое
 * живёт только пока жив компонент, а водитель может свернуть
 * приложение или потерять связь между выбором и отправкой.
 * Сохранением занимается вызывающий (хук useDraftPhotos),
 * сюда приходит уже сохранённая запись.
 *
 * Статус показывается всегда: «сохранено на устройстве» — это
 * тоже результат, и водителю важно видеть, что работа не пропала,
 * даже когда сети нет.
 */

const STATUS_META = {
  local: { label: 'Сохранено на устройстве', tone: 'neutral', icon: 'check' },
  sending: { label: 'Отправляется', tone: 'info', icon: 'trips' },
  sent: { label: 'Отправлено', tone: 'success', icon: 'check' },
  failed: { label: 'Не отправлено', tone: 'danger', icon: 'warning' },
}

export function PhotoUpload({
  label,
  name,
  onChange,
  required = false,
  hint,
  /** Запись из хранилища: { status, previewUrl, error } */
  photo = null,
  disabled = false,
  busy = false,
}) {
  const inputRef = useRef()
  const [localError, setLocalError] = useState(null)

  const status = photo?.status || null
  const meta = status ? STATUS_META[status] : null

  const handleChange = async (e) => {
    const file = e.target.files?.[0]
    // Сбрасываем значение: иначе повторный выбор того же файла
    // не вызовет событие, и переснять кадр будет нельзя.
    e.target.value = ''
    if (!file) return

    setLocalError(null)
    try {
      await onChange?.(name, file)
    } catch (err) {
      setLocalError(err?.message || 'Не удалось сохранить фото')
    }
  }

  const open = () => {
    if (disabled || busy) return
    inputRef.current?.click()
  }

  return (
    <div className="field">
      <label className="field__label" htmlFor={`photo-${name}`}>
        {label}
        {required && (
          <span className="field__required" aria-hidden="true">
            {' '}*
          </span>
        )}
      </label>

      <button
        type="button"
        id={`photo-${name}`}
        className={[
          'photo-upload',
          photo ? 'has-photo' : '',
          disabled ? 'is-disabled' : '',
          busy ? 'is-busy' : '',
        ].join(' ').trim()}
        onClick={open}
        disabled={disabled || busy}
        aria-describedby={meta ? `photo-status-${name}` : undefined}
      >
        {busy && (
          <span className="photo-upload__loading">
            <span className="spinner" />
            <span className="text-muted">Сохраняем…</span>
          </span>
        )}

        {!busy && photo?.previewUrl && (
          <>
            <img src={photo.previewUrl} alt={label} />
            <span className="photo-upload__overlay">
              <Icon name="camera" size={14} />
              Переснять
            </span>
          </>
        )}

        {!busy && !photo && (
          <>
            <Icon name="camera" size={32} />
            <span className="text-muted">{hint || 'Нажмите, чтобы снять'}</span>
          </>
        )}
      </button>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleChange}
        disabled={disabled || busy}
        tabIndex={-1}
        aria-hidden="true"
      />

      {meta && (
        <span
          id={`photo-status-${name}`}
          className={`field__hint field__hint--${meta.tone}`}
          role="status"
        >
          <Icon name={meta.icon} size={12} />
          {meta.label}
        </span>
      )}

      {(localError || photo?.error) && (
        <span className="field__error" role="alert">
          {localError || photo.error}
        </span>
      )}
    </div>
  )
}
